import React, { useState } from "react";
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  SimpleGrid,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
} from "@chakra-ui/react";
import { FileArrowUp, PencilSimple, Plus, Sparkle, Trash } from "@phosphor-icons/react";
import { FIELD_TYPES, PRODUCT_COLUMNS } from "../../suppliersMock";

// Lime-outline pill action, matching the section actions on the form tabs.
const PillAction = ({ icon, children, onClick, solid }) => (
  <Button
    size="sm"
    variant={solid ? "solid" : "outline"}
    borderRadius="full"
    borderColor="limeShade.400"
    bg={solid ? "limeShade.600" : undefined}
    color={solid ? "white" : "limeShade.700"}
    fontWeight="600"
    leftIcon={icon}
    _hover={solid ? { bg: "limeShade.700" } : { bg: "lightLime", borderColor: "limeShade.500" }}
    onClick={onClick}
  >
    {children}
  </Button>
);

const LIME_FOCUS = {
  _hover: { borderColor: "gray.400" },
  _focus: { borderColor: "lime", boxShadow: "0 0 0 1px var(--chakra-colors-lime)" },
  _focusVisible: { borderColor: "lime", boxShadow: "0 0 0 1px var(--chakra-colors-lime)" },
};

const fmtCell = (col, value) => {
  if (value === undefined || value === null || value === "") return "—";
  if (col.type === FIELD_TYPES.NUMBER) return Number(value).toLocaleString("en-GB", { maximumFractionDigits: 2 });
  return value;
};

// Add/edit a product row.
function ProductRowModal({ isOpen, onClose, onSave, row }) {
  const isEdit = !!row;
  const [form, setForm] = useState({});
  React.useEffect(() => { if (isOpen) setForm(row || {}); }, [isOpen, row]);
  const set = (key) => (e) => setForm((p) => ({ ...p, [key]: e.target.value }));
  const isValid = !!String(form.sku || "").trim();

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader color="darkTeal" fontWeight="700">{isEdit ? "Edit product" : "Add product"}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
            {PRODUCT_COLUMNS.map((col) => (
              <FormControl key={col.key} isRequired={col.key === "sku"}>
                <FormLabel fontSize="sm">{col.label}</FormLabel>
                {col.type === FIELD_TYPES.SELECT ? (
                  <Select value={form[col.key] || ""} onChange={set(col.key)} placeholder="Select…" {...LIME_FOCUS}>
                    {col.options.map((o) => <option key={o} value={o}>{o}</option>)}
                  </Select>
                ) : (
                  <Input
                    type={col.type === FIELD_TYPES.NUMBER ? "number" : "text"}
                    value={form[col.key] ?? ""}
                    onChange={set(col.key)}
                    {...LIME_FOCUS}
                  />
                )}
              </FormControl>
            ))}
          </SimpleGrid>
        </ModalBody>
        <ModalFooter gap={3}>
          <Button variant="secondary" borderRadius="full" size="sm" onClick={onClose}>Cancel</Button>
          <Button bg="limeShade.600" color="white" borderRadius="full" _hover={{ bg: "limeShade.700" }} size="sm" isDisabled={!isValid} onClick={() => { onSave(form); onClose(); }}>
            {isEdit ? "Save changes" : "Add product"}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

// Product data collection table — supports multiple product SKUs, styled like
// the scope data-task tables.
function ProductTable({ rows, onChange }) {
  const modal = useDisclosure();
  const [editRow, setEditRow] = useState(null);

  const openAdd = () => { setEditRow(null); modal.onOpen(); };
  const openEdit = (row) => { setEditRow(row); modal.onOpen(); };
  const remove = (id) => onChange(rows.filter((r) => r.id !== id));
  const save = (row) => {
    if (row.id) {
      onChange(rows.map((r) => (r.id === row.id ? { ...r, ...row } : r)));
    } else {
      const nextNum = rows.reduce((max, r) => Math.max(max, Number(String(r.id).split("-").pop()) || 0), 0) + 1;
      onChange([...rows, { ...row, id: `prod-new-${nextNum}` }]);
    }
  };

  return (
    <Box>
      <Flex justify="flex-start" mb={4} gap={3} flexWrap="wrap">
        <PillAction icon={<Plus size={15} weight="bold" />} onClick={openAdd}>Add product</PillAction>
        <PillAction icon={<FileArrowUp size={15} />}>Import from Template</PillAction>
        <PillAction icon={<Sparkle size={15} weight="fill" />}>AI Mapping</PillAction>
      </Flex>

      <Box border="1px solid" borderColor="gray.200" borderRadius="md" overflow="hidden" bg="white" overflowX="auto">
        <Table size="sm" variant="unstyled" minW="900px">
          <Thead>
            <Tr bg="gray.50">
              {PRODUCT_COLUMNS.map((col) => (
                <Th key={col.key} py={3} px={4} fontSize="11px" color="gray.500" textTransform="uppercase" letterSpacing="0.04em" fontWeight="600" textAlign={col.align === "right" ? "right" : "left"}>
                  {col.label}
                </Th>
              ))}
              <Th py={3} px={4} width="80px" />
            </Tr>
          </Thead>
          <Tbody>
            {rows.length === 0 ? (
              <Tr><Td colSpan={PRODUCT_COLUMNS.length + 1} py={6} px={4} textAlign="center"><Text fontSize="14px" color="gray.400">No products added yet.</Text></Td></Tr>
            ) : rows.map((row, idx) => (
              <Tr key={row.id} borderTop={idx === 0 ? "none" : "1px solid"} borderColor="gray.100" _hover={{ bg: "gray.50" }}>
                {PRODUCT_COLUMNS.map((col) => (
                  <Td key={col.key} py={3} px={4} fontSize="14px" color="gray.700" fontWeight={col.key === "sku" ? "600" : "400"} textAlign={col.align === "right" ? "right" : "left"}>
                    {fmtCell(col, row[col.key])}
                  </Td>
                ))}
                <Td py={3} px={4} textAlign="right">
                  <Flex justify="flex-end" gap={0}>
                    <IconButton aria-label="Edit product" icon={<PencilSimple size={14} />} size="sm" variant="ghost" color="gray.500" _hover={{ color: "limeShade.600", bg: "lightLime" }} onClick={() => openEdit(row)} />
                    <IconButton aria-label="Delete product" icon={<Trash size={14} />} size="sm" variant="ghost" color="gray.500" _hover={{ color: "red.500", bg: "red.50" }} onClick={() => remove(row.id)} />
                  </Flex>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>

      <ProductRowModal isOpen={modal.isOpen} onClose={modal.onClose} onSave={save} row={editRow} />
    </Box>
  );
}

export default ProductTable;
